import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FinanceBucket, LineKind } from '@garage/shared'
import { LINE_KIND_LABELS } from '@garage/shared'
import type { Formatters } from '@/lib/format.js'

interface CategoryProfitChartProps {
  byCategory: Array<FinanceBucket & { kind: LineKind }>
  formatters: Formatters
}

// The viewBox is set to the wrapper's own measured pixel WIDTH (see the
// ResizeObserver below), so one viewBox unit is always exactly one on-screen
// pixel and every constant below can be a plain CSS pixel value. Previously
// the chart laid rows out in a 0-100 box and rescaled x and y by different
// factors to reach a hardcoded 1000x700 viewBox, which is why the category
// names ended up stacked at the bottom instead of beside their bars: their
// position was computed in one coordinate space and rendered in another.
const PADDING = { top: 12, right: 88, bottom: 12, left: 132 }
const CORNER_RADIUS = 4
const MAX_BAR_THICKNESS = 32
const DEFAULT_WIDTH = 600

// Width:height ratio, mirrored in the `aspect-[…]` class below. Height is a
// pure function of width — never the other way round — because reading a
// height back out of an element whose height also depends on what we draw
// inside it is an unbounded feedback loop: that is what previously inflated
// this panel to thousands of pixels tall.
const ASPECT_RATIO = 9 / 4

interface HoverState {
  kind: LineKind | null
}

/** Path for a rectangle rounded on its right two corners only (the "data" end), clamped to the shape's size. */
function roundedRightRect(x: number, top: number, width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, height / 2, width / 2))
  if (width <= 0) return ''
  return `
    M ${x} ${top}
    L ${x + width - r} ${top}
    Q ${x + width} ${top} ${x + width} ${top + r}
    L ${x + width} ${top + height - r}
    Q ${x + width} ${top + height} ${x + width - r} ${top + height}
    L ${x} ${top + height}
    Z
  `
}

/**
 * Horizontal bar chart of profit by category (Parts, Services, Materials).
 * Renders as hand-written inline SVG; no external charting library.
 * Bars are pre-sorted in LINE_KINDS order, as per the schema contract.
 */
export function CategoryProfitChart({ byCategory, formatters }: CategoryProfitChartProps) {
  const { t } = useTranslation()
  const kindLabel = (kind: LineKind) => t(`catalog.kinds.${kind}`, LINE_KIND_LABELS[kind])
  const [hover, setHover] = useState<HoverState>({ kind: null })
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [measuredWidth, setMeasuredWidth] = useState(DEFAULT_WIDTH)

  // Observe WIDTH only — see the ASPECT_RATIO comment above for why.
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w && w > 0) setMeasuredWidth(w)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const width = measuredWidth
  const height = width / ASPECT_RATIO
  const chartWidth = Math.max(width - PADDING.left - PADDING.right, 1)
  const chartHeight = Math.max(height - PADDING.top - PADDING.bottom, 1)
  const rowHeight = chartHeight / (byCategory.length || 1)
  const barThickness = Math.min(rowHeight * 0.5, MAX_BAR_THICKNESS)

  // Find max profit for scaling; bars never divide by zero even if every category is flat.
  const maxProfit = Math.max(...byCategory.map((c) => c.profitMinor), 1)

  const colorVars: Record<LineKind, string> = {
    part: 'var(--series-part)',
    service: 'var(--series-service)',
    material: 'var(--series-material)',
  }

  const hoveredData = hover.kind !== null ? byCategory.find((c) => c.kind === hover.kind) : null

  return (
    <div className="w-full flex flex-col gap-3">
      <style>{`
        .chart-category-profit {
          --series-part: #3987e5;
          --series-service: #d95926;
          --series-material: #199e70;
        }
        :root.light .chart-category-profit {
          --series-part: #2a78d6;
          --series-service: #eb6834;
          --series-material: #1baf7a;
        }
      `}</style>

      <div ref={wrapperRef} className="w-full aspect-[9/4] min-h-[160px]">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-full chart-category-profit"
        role="img"
        aria-label={t('financeChart.profitByCategoryChartLabel')}
        onMouseLeave={() => setHover({ kind: null })}
      >
        {/* Background */}
        <rect width={width} height={height} fill="var(--c-surface-lowest)" />

        {/* Shared left baseline all bars grow from */}
        <line
          x1={PADDING.left}
          y1={PADDING.top}
          x2={PADDING.left}
          y2={height - PADDING.bottom}
          stroke="var(--c-outline-variant)"
          strokeWidth={1}
        />

        {/* Rows: category label on the left, bar growing right, value label past the bar */}
        {byCategory.map((bucket, idx) => {
          const yCenter = PADDING.top + (idx + 0.5) * rowHeight
          const barTop = yCenter - barThickness / 2
          const barWidth = Math.max((bucket.profitMinor / maxProfit) * chartWidth * 0.95, 0)
          const colorValue = colorVars[bucket.kind] ?? 'var(--c-on-surface)'

          return (
            <g key={bucket.kind}>
              {barWidth > 0 && (
                <path
                  d={roundedRightRect(PADDING.left, barTop, barWidth, barThickness, CORNER_RADIUS)}
                  fill={colorValue}
                />
              )}

              {/*
                Value label sits past the bar's end against the panel background, not
                overlaid on the coloured fill — one of the series colours (light-mode
                aqua) fails a 3:1 contrast check against on-surface text, so the label
                must never depend on the bar colour for legibility.
              */}
              <text
                x={PADDING.left + barWidth + 8}
                y={yCenter}
                dominantBaseline="middle"
                textAnchor="start"
                fill="currentColor"
                className="text-label-sm text-on-surface font-semibold"
                pointerEvents="none"
              >
                {formatters.amount(bucket.profitMinor)}
              </text>

              {/* Category label, left of its own bar */}
              <text
                x={PADDING.left - 10}
                y={yCenter}
                dominantBaseline="middle"
                textAnchor="end"
                fill="currentColor"
                className="text-label-md text-on-surface font-medium"
                pointerEvents="none"
              >
                {kindLabel(bucket.kind)}
              </text>

              {/* Hit target: full row height, from the baseline to the right edge */}
              <rect
                x={PADDING.left}
                y={PADDING.top + idx * rowHeight}
                width={chartWidth}
                height={rowHeight}
                fill="transparent"
                onMouseEnter={() => setHover({ kind: bucket.kind })}
              />
            </g>
          )
        })}

        {/* Tooltip, anchored to the hovered row and clamped inside the plot */}
        {hoveredData &&
          (() => {
            const idx = byCategory.findIndex((c) => c.kind === hoveredData.kind)
            const yCenter = PADDING.top + (idx + 0.5) * rowHeight
            const barWidth = Math.max((hoveredData.profitMinor / maxProfit) * chartWidth * 0.95, 0)
            const barEnd = PADDING.left + barWidth
            const tooltipWidth = 168
            const tooltipHeight = 88
            // Prefer sitting just past the bar's end; clamp so it never runs off
            // either edge of the panel (this is what the first/last-row check exercises).
            const tooltipX = Math.min(Math.max(barEnd + 12, PADDING.left), width - tooltipWidth - 4)
            const tooltipY = Math.min(Math.max(yCenter - tooltipHeight / 2, PADDING.top), height - PADDING.bottom - tooltipHeight)

            return (
              <g pointerEvents="none">
                <rect
                  x={tooltipX}
                  y={tooltipY}
                  width={tooltipWidth}
                  height={tooltipHeight}
                  rx={6}
                  fill="var(--c-surface-highest)"
                  stroke="var(--c-outline-variant)"
                  strokeWidth={1}
                />
                <text x={tooltipX + 12} y={tooltipY + 20} fill="currentColor" className="text-label-md text-on-surface font-semibold">
                  {kindLabel(hoveredData.kind)}
                </text>
                <text x={tooltipX + 12} y={tooltipY + 39} fill="currentColor" className="text-label-sm text-on-surface-variant">
                  {t('financeChart.revenue')}: {formatters.amount(hoveredData.revenueMinor)}
                </text>
                <text x={tooltipX + 12} y={tooltipY + 56} fill="currentColor" className="text-label-sm text-on-surface-variant">
                  {t('financeChart.cost')}: {formatters.amount(hoveredData.costMinor)}
                </text>
                <text x={tooltipX + 12} y={tooltipY + 73} fill="currentColor" className="text-label-sm text-on-surface-variant">
                  {t('financeChart.margin')}: {hoveredData.marginPct.toFixed(1)}%
                </text>
              </g>
            )
          })()}
      </svg>
      </div>
    </div>
  )
}
